import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiX, FiSearch, FiPlus } from 'react-icons/fi';

const EditGodGroupForm = ({ group, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        groupName: group.groupName,
        handler: group.groupMembers?.handler || null,
        participants: group.groupMembers?.participants?.map(p => ({
            joinDate: p.joinDate ? new Date(p.joinDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            memberID: p.memberID._id,
            memberFullName: p.memberID.fullName,
            removeDate: p.removeDate ? new Date(p.removeDate).toISOString().split('T')[0] : null,
            remarks: p.remarks || ''
        })) || [],
        renewalDate: group.renewalDate ? new Date(group.renewalDate).toISOString().split('T')[0] : ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [members, setMembers] = useState([]);
    
    // Separate search states for handler and participants
    const [handlerSearchTerm, setHandlerSearchTerm] = useState('');
    const [participantSearchTerm, setParticipantSearchTerm] = useState('');
    const [showHandlerSearch, setShowHandlerSearch] = useState(false);
    const [showParticipantSearch, setShowParticipantSearch] = useState(false);

    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    // Fetch all active members for selection
    const fetchMembers = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No auth token found');
                setError('Authentication required');
                return;
            }

            const baseUrl = process.env.REACT_APP_API_URL;
            const apiUrl = `${baseUrl}/api/godmembers`;

            const response = await axios.get(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'ngrok-skip-browser-warning': 'true'
                }
            });

            if (response.data.success) {
                setMembers(response.data.data);
            } else {
                setError(response.data.message || 'Failed to fetch members');
            }
        } catch (err) {
            setError(`Failed to fetch members: ${err.response?.data?.error || err.message}`);
        }
    };

    useEffect(() => {
        fetchMembers();
    }, []);

    useEffect(() => {
        console.log('Members:', members);
        console.log('Participants:', formData.participants);
    }, [members, formData.participants]);

    const searchMembers = useCallback(async (searchTerm) => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            const apiUrl = `${baseUrl}/api/godmembers/search?term=${encodeURIComponent(searchTerm.trim())}`;
            const response = await axios.get(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'ngrok-skip-browser-warning': 'true'
                }
            });
            if (response.data.success) {
                setSearchResults(response.data.data);
            } else {
                setSearchResults([]);
            }
        } catch (err) {
            console.error('Search failed:', err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, []);

    const runHandlerSearch = () => {
        if (!handlerSearchTerm.trim()) {
            setSearchResults([]);
            return;
        }
        setShowHandlerSearch(true);
        searchMembers(handlerSearchTerm);
    };

    const runParticipantSearch = () => {
        if (!participantSearchTerm.trim()) {
            setSearchResults([]);
            return;
        }
        setShowParticipantSearch(true);
        searchMembers(participantSearchTerm);
    };

    // Update search result displays
    const renderSearchResults = (onSelect) => {
        if (isSearching) {
            return <div className="p-2 text-gray-500">Searching...</div>;
        }

        if (searchResults.length === 0) {
            return <div className="p-2 text-gray-500">No results found</div>;
        }

        return searchResults.map(member => (
            <div
                key={member._id}
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => onSelect(member)}
            >
                {member.fullName}
            </div>
        ));
    };

    const handleSelectHandler = (member) => {
        setFormData(prev => ({
            ...prev,
            handler: member
        }));
        setHandlerSearchTerm('');
        setShowHandlerSearch(false);
    };

    const handleSelectParticipant = (member) => {
        const newParticipant = {
            joinDate: new Date().toISOString().split('T')[0],
            memberID: member._id,
            memberFullName: member.fullName,
            removeDate: null,
            remarks: ''
        };

        setFormData(prev => ({
            ...prev,
            participants: [...prev.participants, newParticipant]
        }));
        setParticipantSearchTerm('');
        setShowParticipantSearch(false);
    };

    const handleRemoveParticipant = (index) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.filter((_, i) => i !== index)
        }));
    };

    const handleParticipantDateChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.map((p, i) => 
                i === index 
                    ? { ...p, [field]: value }
                    : p
            )
        }));
    };

    const handleParticipantRemarksChange = (index, value) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.map((p, i) => 
                i === index 
                    ? { ...p, remarks: value }
                    : p
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/godgroups/${group._id}`,
                {
                    groupName: formData.groupName,
                    groupMembers: {
                        handler: formData.handler._id,
                        participants: formData.participants
                    },
                    renewalDate: formData.renewalDate
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                onSuccess && onSuccess(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update god group');
        } finally {
            setLoading(false);
        }
    };

    const renderHandlerSearch = () => (
        <div className="mt-1">
            <div className="flex gap-2">
                <input
                    type="text"
                    placeholder="GodID or name, then Enter or click Search"
                    value={handlerSearchTerm}
                    onChange={(e) => {
                        setHandlerSearchTerm(e.target.value);
                        setSearchResults([]);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            runHandlerSearch();
                        }
                    }}
                    onFocus={() => setShowHandlerSearch(true)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                />
                <button
                    type="button"
                    onClick={runHandlerSearch}
                    className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                    aria-label="Search handler"
                >
                    {isSearching ? '...' : <FiSearch />}
                </button>
            </div>
            {showHandlerSearch && handlerSearchTerm.trim() !== '' && (
                <div className="mt-2 border rounded-md max-h-40 overflow-y-auto">
                    {renderSearchResults(handleSelectHandler)}
                </div>
            )}
        </div>
    );

    const renderParticipantSearch = () => (
        <div className="flex gap-2 mb-2">
            <input
                type="text"
                placeholder="GodID or name, then Enter or click Search"
                value={participantSearchTerm}
                onChange={(e) => {
                    setParticipantSearchTerm(e.target.value);
                    setSearchResults([]);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        runParticipantSearch();
                    }
                }}
                onFocus={() => setShowParticipantSearch(true)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
            />
            <button
                type="button"
                onClick={runParticipantSearch}
                className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                aria-label="Search participant"
            >
                {isSearching ? '...' : <FiSearch />}
            </button>
            {showParticipantSearch && participantSearchTerm.trim() !== '' && (
                <div className="absolute mt-10 w-full border rounded-md max-h-40 overflow-y-auto bg-white z-50">
                    {renderSearchResults(handleSelectParticipant)}
                </div>
            )}
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Edit God Group</h3>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                        <FiX />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Group Name</label>
                        <input
                            type="text"
                            value={formData.groupName}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                groupName: e.target.value
                            }))}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Handler</label>
                        {formData.handler ? (
                            <div className="mt-1 flex items-center justify-between p-2 border rounded-md">
                                <span>{formData.handler.fullName}</span>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, handler: null }))}
                                    className="text-red-600 hover:text-red-800"
                                >
                                    <FiX />
                                </button>
                            </div>
                        ) : renderHandlerSearch()}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Participants</label>
                        <div className="mt-1 relative">
                            {renderParticipantSearch()}
                            <div className="space-y-2">
                                {formData.participants.map((participant, index) => (
                                    <div key={index} className="space-y-2 p-4 border rounded-md">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium">
                                                {participant.memberFullName}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveParticipant(index)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Join Date</label>
                                                <input
                                                    type="date"
                                                    value={participant.joinDate}
                                                    onChange={(e) => handleParticipantDateChange(index, 'joinDate', e.target.value)}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Remove Date</label>
                                                <input
                                                    type="date"
                                                    value={participant.removeDate || ''}
                                                    onChange={(e) => handleParticipantDateChange(index, 'removeDate', e.target.value)}
                                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Remarks</label>
                                            <textarea
                                                value={participant.remarks}
                                                onChange={(e) => handleParticipantRemarksChange(index, e.target.value)}
                                                rows="2"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                                                placeholder="Add any remarks here..."
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Renewal Date</label>
                        <input
                            type="date"
                            value={formData.renewalDate}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                renewalDate: e.target.value
                            }))}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold"
                        />
                    </div>

                    <div className="flex justify-end space-x-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !formData.handler || formData.participants.length === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditGodGroupForm; 